import {
  Box,
  Container,
  Divider,
  IconButton,
  InputLabel,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DateDropZone from '../DropZones/DateDropZone';
import DatePicker from '../../DatePicker/DatePicker';
import TextField from '@mui/material/TextField';
import { resetSource, setSource } from '../../../redux/Calculations/CalculationsActions';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { fontSize } from '../../..';
import { checkIfMissingColumn, handleKeyDown } from '../../CommonFunctions/CommonFunctions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { useDispatch } from 'react-redux';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { CanvasSelect } from '../common/CanvasSelect';
interface IAddDateInterval {
  propKey: string;
  isFlowUsedInOtherFlowsAsSource: boolean;
}

type IntervalOption = {
  value: 'day' | 'week' | 'month' | 'year';
  label: string;
};

const AddDateInterval = ({ propKey, isFlowUsedInOtherFlowsAsSource }: IAddDateInterval) => {
  const { calculations, tablesForSelectedDataSets, chartProperties } = useSelector(
    (state: RootState) => {
      return {
        calculations: state.calculations,
        tablesForSelectedDataSets: state.tabTileProps.tablesForSelectedDataSets,
        chartProperties: state.chartProperties,
      };
    },
  );
  const dispatch = useDispatch();
  const currCalculation = calculations.properties[propKey];
  const intervalTypes: IntervalOption[] = [
    {
      value: 'day',
      label: 'Day',
    },
    {
      value: 'week',
      label: 'Week',
    },
    {
      value: 'month',
      label: 'Month',
    },
    {
      value: 'year',
      label: 'Year',
    },
  ];
  const valueTypes = ['text', 'decimal', 'integer', 'boolean', 'date'];
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

  const activeFlow = currCalculation.currentCalculationSession?.activeFlow!;
  const calculationInfo = currCalculation.currentCalculationSession?.calculationInfo!;
  const source = calculationInfo?.flows[activeFlow][0].source!;
  const sourceType = calculationInfo?.flows[activeFlow][0]!.sourceType!;
  const [input, setInput] = useState<{
    intervalType: string;
    intervalValue: string;
  }>({
    intervalType: source[2],
    intervalValue: source[1],
  });
  useEffect(() => {
    if (source?.length > 0) {
      setInput({
        intervalType: source[2],
        intervalValue: source[1],
      });
    }
  }, [source]);
  const getName = (): string | null => {
    if (sourceType[0] === 'field') return calculationInfo.fields[source[0]].displayName;
    else if (sourceType[0] === 'flow') {
      if (!currCalculation.currentCalculationSession) return null;

      return currCalculation.currentCalculationSession!.flowDisplayNames[source[0]];
    }
    return source[0];
  };

  const selectedInterval =
    intervalTypes.find((t) => t.value === input.intervalType) ?? intervalTypes[0];

  const selectedDs = chartProperties.properties[propKey].selectedDs;

  useEffect(() => {
    if (sourceType[0] !== 'field') return;
    (async () => {
      try {
        const field = calculationInfo?.fields[source[0]];
        const res = await checkIfMissingColumn(
          field,
          selectedDs,
          tablesForSelectedDataSets[selectedDs.id],
        );
        setIsShowWarning(res.isMissing);
      } catch (error) {
        console.error('Error checking missing column:', error);
        setIsShowWarning(true);
      }
    })();
  }, [sourceType]);

  return (
    <Container
      disableGutters
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // paddingInline: "0.5rem",
        margin: 0,
      }}
      maxWidth="xs"
    >
      <Box
        sx={{
          padding: '0',
          margin: '0',
          fontWeight: 'bold',
          // color: "gray",
          textAlign: 'start',
          display: 'flex',
          gap: '10px',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            fontWeight: 'bold',
            // color: "gray",
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: fontSize.large,
              paddingLeft: '8px',
              // color: "gray",
            }}
          >
            Date Add
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '11px',
              color: '#999999',
              marginLeft: '0.3rem',
              fontWeight: 'bold',
            }}
          >
            ({source?.length === 3 ? 1 : 0} of 1/1)
          </Typography>
        </Box>
        <Tooltip
          title="Add Custom Date"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
            }}
            disabled={isFlowUsedInOtherFlowsAsSource}
          >
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          {valueTypes.map((type, index) => {
            return (
              <MenuItem
                key={index}
                sx={{ width: '7rem' }}
                disabled={type !== 'date'}
                value={type}
                onClick={() => {
                  setAnchorEl(null);
                  dispatch(
                    setSource(
                      propKey,
                      activeFlow,
                      0,
                      new Date().toISOString().split('T')[0],
                      'date',
                      0,
                    ),
                  );
                  dispatch(setSource(propKey, activeFlow, 0, 3, 'integer', 1));
                  dispatch(setSource(propKey, activeFlow, 0, 'day', 'text', 2));
                }}
              >
                {type.replace(/\b\w/g, (char) => char.toUpperCase())}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>

      <DateDropZone
        propKey={propKey}
        flow={calculationInfo?.flows[activeFlow][0]!.flow}
        disableDrop={isFlowUsedInOtherFlowsAsSource}
        sx={
          source.length > 0
            ? {
                flexGrow: 0,
                height: '5rem',
              }
            : {}
        }
      >
        {source.length === 0 ? (
          <p
            style={{ fontSize: '11px', color: '#999999', paddingLeft: '8px', textAlign: 'center' }}
          >
            Drop a column or click +<br /> to add static values
          </p>
        ) : (
          <>
            <ListItem
              className="axisFilterField"
              sx={{
                borderRadius: '5px',
                border: isShowWarning ? '1px solid #850000ff !important' : '',
                marginBlock: '5px',
                marginLeft: '0.5rem',
                paddingLeft: '0.3rem',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '24px', // Match this height with TextField height if needed
                width: '95%',
                gap: '0.2rem',
              }}
            >
              {!isFlowUsedInOtherFlowsAsSource && (
                <button
                  type="button"
                  className="buttonCommon columnClose"
                  onClick={() => {
                    dispatch(resetSource(propKey, activeFlow, 0));
                  }}
                  title="Remove field"
                >
                  <CloseRoundedIcon style={{ fontSize: '13px' }} />
                </button>
              )}
              {isFlowUsedInOtherFlowsAsSource && (
                <Tooltip
                  title="This calculation is being used in another flow. Please remove the dependency before deleting."
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <LockOutlinedIcon
                    style={{
                      color: '#2BB9BB',
                    }}
                  />
                </Tooltip>
              )}
              {sourceType[0] === 'date' ? (
                <DatePicker
                  value={source[0]}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    dispatch(
                      setSource(
                        propKey,
                        activeFlow,
                        0,
                        newValue.toISOString().split('T')[0],
                        'date',
                        0,
                      ),
                    );
                  }}
                  disabled={isFlowUsedInOtherFlowsAsSource}
                />
              ) : sourceType[0] === 'field' || sourceType[0] === 'flow' ? (
                <ListItemText
                  primary={getName()}
                  sx={{ '& .MuiListItemText-primary': { fontSize: '11px' } }}
                />
              ) : null}
            </ListItem>
          </>
        )}
      </DateDropZone>

      {source.length > 0 ? (
        <>
          <Divider />
          <Box
            sx={{
              cursor: 'pointer',
              textAlign: 'left',
              paddingBottom: '10px',
              minHeight: '4rem',
              marginTop: '0',
            }}
          >
            <InputLabel
              sx={{
                marginLeft: '0.5rem',
                marginTop: '0.5rem',
                // color: "gray",
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              Time Period
            </InputLabel>
            <CanvasSelect<IntervalOption>
              value={selectedInterval}
              options={intervalTypes}
              placeholder="Select"
              getLabel={(opt) => opt.label}
              getKey={(opt) => opt.value}
              onChange={(opt) => {
                setInput((prev) => ({ ...prev, intervalType: opt.value }));
                dispatch(setSource(propKey, activeFlow, 0, opt.value, 'text', 2));
              }}
              disabled={isFlowUsedInOtherFlowsAsSource}
              style={{
                width: '95%',
                marginLeft: '0.5rem',
                marginTop: '0.5rem',
                height: '2rem',
                fontSize: '11px',
              }}
            />

            <InputLabel
              sx={{
                marginLeft: '0.5rem',
                marginTop: '0.5rem',
                fontSize: '12px',
                // color: "gray",
                fontWeight: 'bold',
              }}
            >
              Number of {input.intervalType}/s
            </InputLabel>
            <TextField
              error={!source[1]}
              id="outlined-number"
              type="number"
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(event)}
              value={input.intervalValue}
              onChange={(e) => {
                setInput((prev) => ({ ...prev, intervalValue: e.target.value }));
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onBlur={(e) => {
                dispatch(setSource(propKey, activeFlow, 0, e.target.value, 'integer', 1));
              }}
              sx={{
                width: '95%',
                marginLeft: '0.5rem',
                '& .MuiInputBase-root': {
                  height: '2rem',
                  fontSize: '11px',
                },
                '& .MuiTextField-root': {
                  outline: 'none',
                  border: 'none',
                },
                border: 'none',
                marginTop: '0.5rem',
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#2bb9bb',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '16.5px 14px', // Consistent padding for all browsers
                },
                // Firefox-specific styling
                '@supports (-moz-appearance: none)': {
                  '& .MuiOutlinedInput-input': {
                    padding: '16.5px 2px 16.5px 14px',
                  },
                },
              }}
              disabled={isFlowUsedInOtherFlowsAsSource}
            />
          </Box>
        </>
      ) : null}
    </Container>
  );
};

export default AddDateInterval;
